/**
 * DataTableClientBlockRenderer
 *
 * Renders `data_table_client` blocks with an expand-to-dialog experience that
 * preserves table state (search, sort, pagination) across expand/collapse.
 *
 * Architecture:
 * - The inline card and the Dialog each render a `DataTable variant="client"`,
 *   but both share the same controlled state (sorting, globalFilter, paginationIndex)
 *   via useState at this component level.
 * - When the user expands, the Dialog mounts a fresh DataTable seeded with the
 *   current state. Any changes in the Dialog flow back to the same state, so
 *   collapsing preserves where the user left off.
 *
 * Supports optional page navigation via ReportNavigationContext:
 * - Rows with `__page_ref` are clickable and navigate to the referenced page
 * - `__page_ref` is not rendered as a visible column
 * - If no navigation context is available (e.g., flat reports, chat), rows render as normal
 */

import { useState, useMemo, useCallback } from 'react';
import { Maximize2 } from 'lucide-react';
import {
  cn,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@strivelabs/ui-core';
import type { ColumnDef, SortingFn, SortingState } from '@tanstack/react-table';
import type { DataTableClientBlock, DataTableClientRow, DataTableClientSortType, ColoredCell, MetricComparisonCell } from '@/types/ui-blocks';
import { useReportNavigation } from '@/contexts/ReportNavigationContext';
import { useTableBlockAddon } from '@/contexts/TableBlockAddonContext';

/** Type guard: checks if a cell value is a MetricComparisonCell. */
function isMetricComparisonCell(val: unknown): val is MetricComparisonCell {
  return typeof val === 'object' && val !== null
    && 'value' in val && 'change' in val && 'change_percentage' in val;
}

/** Type guard: checks if a cell value is a `{ value, color? }` object. */
function isColoredCell(val: unknown): val is ColoredCell {
  return typeof val === 'object' && val !== null && 'value' in val && !('page_group_id' in val) && !('change_percentage' in val);
}

/** Extracts the sortable string from a raw cell value (unwraps ColoredCell/MetricComparisonCell). */
function extractSortValue(raw: unknown): string {
  if (isMetricComparisonCell(raw)) return String(raw.value ?? '');
  if (isColoredCell(raw)) return String(raw.value ?? '');
  return String(raw ?? '');
}

/** Strips non-numeric characters (except `.` and `-`) and parses to float. */
function parseNumeric(s: string): number {
  return parseFloat(s.replace(/[^0-9.-]/g, '')) || 0;
}

/** Returns a TanStack sortingFn appropriate for the given sortType. */
function makeSortingFn(sortType: DataTableClientSortType): SortingFn<DataTableClientRow> {
  return (rowA, rowB, columnId) => {
    const a = extractSortValue(rowA.getValue(columnId));
    const b = extractSortValue(rowB.getValue(columnId));
    if (sortType === 'currency' || sortType === 'percentage') {
      return parseNumeric(a) - parseNumeric(b);
    }
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  };
}

export interface DataTableClientBlockRendererProps {
  block: DataTableClientBlock;
}

export function DataTableClientBlockRenderer({ block }: DataTableClientBlockRendererProps) {
  const { content } = block;
  const navigation = useReportNavigation();
  const renderAddon = useTableBlockAddon();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // ---- Shared controlled state (survives expand/collapse) ----
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [paginationIndex, setPaginationIndex] = useState(0);

  // Build TanStack ColumnDef[] from the contract columns
  // First column of navigable rows gets hyperlink styling (text-blue-600 hover:underline)
  const columns = useMemo((): ColumnDef<DataTableClientRow>[] => {
    return content.columns.map((col, colIndex) => ({
      accessorKey: col.key,
      header: col.label,
      enableSorting: col.enableSorting ?? false,
      ...(col.enableSorting && col.sortType ? { sortingFn: makeSortingFn(col.sortType) } : {}),
      cell: ({ row }) => {
        const rawCellValue = row.original[col.key];

        // MetricComparisonCell → "value vs change (change%)"
        if (isMetricComparisonCell(rawCellValue)) {
          const val = rawCellValue.value ?? '—';
          if (rawCellValue.change === null) return String(val);
          const changePct = rawCellValue.change_percentage;
          return (
            <span className="whitespace-nowrap">
              {String(val)} vs {String(rawCellValue.change)}
              {changePct && (
                <span style={{ color: rawCellValue.color }}>{` (${changePct})`}</span>
              )}
            </span>
          );
        }

        // Extract display value and optional color from ColoredCell objects
        const cellValue = isColoredCell(rawCellValue) ? rawCellValue.value : rawCellValue;
        const cellColor = isColoredCell(rawCellValue) ? rawCellValue.color : undefined;
        const display = cellValue === null || cellValue === undefined ? '—' : String(cellValue);

        // First column + navigable row → render as hyperlink
        if (colIndex === 0 && navigation && row.original.__page_ref) {
          return <span className="text-blue-600 hover:underline">{display}</span>;
        }

        if (cellColor) {
          return <span style={{ color: cellColor }}>{display}</span>;
        }

        return display;
      },
    }));
  }, [content.columns, navigation]);

  const rows = content.rows;

  // Only enable row clicks when navigation context exists AND at least one row has a page ref
  const hasNavigableRows = useMemo(
    () => navigation !== null && rows.some((r) => r.__page_ref),
    [navigation, rows]
  );

  const handleRowClick = useCallback(
    (row: DataTableClientRow) => {
      if (navigation && row.__page_ref) {
        navigation.navigateToPage(row.__page_ref);
      }
    },
    [navigation]
  );

  const isEmpty = rows.length === 0;

  // Enable horizontal scroll + first-column pinning for wide tables
  const isWideTable = content.columns.length > 5;

  // ---- Shared DataTable props (passed to both inline and dialog instances) ----
  const sharedDataTableProps = {
    columns,
    data: rows,
    enableSearch: content.enableSearch ?? false,
    searchPlaceholder: content.searchPlaceholder,
    fuzzySearch: content.enableSearch ?? false,
    enablePagination: content.enablePagination ?? false,
    pageSize: content.pageSize ?? 10,
    emptyText: content.emptyText ?? 'No results found',
    bordered: false as const,
    horizontalScroll: isWideTable,
    pinFirstColumn: isWideTable,
    onRowClick: hasNavigableRows ? handleRowClick : undefined,
    // Controlled state — both instances share the same values
    sorting,
    onSortingChange: setSorting,
    globalFilter,
    onGlobalFilterChange: setGlobalFilter,
    paginationIndex,
    onPaginationChange: setPaginationIndex,
  };

  const addon = useMemo(() => renderAddon?.(block) ?? null, [renderAddon, block]);

  return (
    <>
      {/* Main card view — matches TableWrapper / ChartWrapper card styling */}
      <div
        className={cn(
          'flex flex-col gap-4 bg-custom-bg-primary border border-custom-border rounded-lg p-4 relative',
        )}
      >
        {/* Expand button — top-right, matching TableWrapper */}
        {!isEmpty && (
          <Button
            variant="ghost"
            size="iconSm"
            onClick={() => setIsDialogOpen(true)}
            className="absolute top-3 right-3 z-10 bg-custom-card-bg/60 border border-custom-border shadow-sm hover:bg-custom-card-bg/80 cursor-pointer"
            aria-label="Expand table"
          >
            <Maximize2 className="size-3.5 text-muted-foreground" />
          </Button>
        )}

        {/* Header — title/description + optional addon (e.g., date range picker) */}
        {(content.title || content.description || addon) && (
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="flex flex-col gap-1 min-w-0">
              {content.title && (
                <h3 className="text-sm text-custom-text-secondary font-semibold leading-tight tracking-tight">
                  {content.title}
                </h3>
              )}
              {content.description && (
                <p className="text-sm text-muted-foreground">{content.description}</p>
              )}
            </div>
            {addon}
          </div>
        )}

        {/* Inline DataTable */}
        <div className="w-full overflow-auto p-px">
          <DataTable<DataTableClientRow>
            variant="client"
            {...sharedDataTableProps}
          />
        </div>
      </div>

      {/* Expanded dialog — matches TableWrapper's Dialog exactly */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-7xl w-[95vw] max-h-[90vh] overflow-auto">
          <DialogHeader>
            {content.title && <DialogTitle>{content.title}</DialogTitle>}
            {content.description && <DialogDescription>{content.description}</DialogDescription>}
          </DialogHeader>
          <div className="mt-4" style={{ maxHeight: '70vh' }}>
            <DataTable<DataTableClientRow>
              variant="client"
              {...sharedDataTableProps}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
